import { createStyles, withStyles, WithStyles } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import gql from 'graphql-tag';
import compose from 'lodash/flowRight';
import React, { useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import { loader } from 'graphql.macro';
import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/types/dist/severity';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { VideoPrompt } from 'src/components/Dialogs/VideoPrompt';
import { Dispatch } from 'src/config/Store';
import { VideoProvider } from 'src/models/FeatureFlags';

import { ReactComponent as RefreshIcon } from '../assets/svgs/refresh.svg';
import ReportVisitDialog from '../components/Dialogs/ReportVisit';
import ReportVisitConfirmation from '../components/Dialogs/ReportVisitConfirmation';
import DefaultError from '../components/Errors/DefaultError';
import MemberProfileDrawer from '../components/MemberProfileDrawer/MemberProfileDrawer';
import MHButton from '../components/MHButton';
import PageContainer from '../components/PageContainer';
import VideoSession from '../components/VideoSession';
import { memberClient } from '../config/Apollo';
import { PresenceState } from '../constants';
import { buildMemberChatPath } from '../helpers/pathHelpers';
import { DEFAULT_BOX_SHADOW } from '../themes';
import { colors } from '../themes/colors';

const WAITING_FOR_CLIENT_PAGE_QUERY = loader(
  'src/components/Sessions/documents/WaitingForClientPage.gql',
);

const MIN_WIDTH_SCREEN_SIZE = '599px';

const styles = () =>
  createStyles({
    errorButtons: {
      display: 'flex',
      justifyContent: 'space-between',

      '& div': {
        // in order to have the buttons be equal distance apart
        width: '48%',
      },
    },
    reconnectButton: {
      backgroundColor: colors.turquoise1,
      color: 'white',
      '&:hover': {
        backgroundColor: colors.turquoise1,
        color: 'white',
        boxShadow: DEFAULT_BOX_SHADOW,
      },
      '& svg': {
        marginRight: '8px',
      },
    },
    endVideoSessionButton: {
      backgroundColor: colors.gray16,
      color: 'white',
      '&:hover': {
        backgroundColor: colors.gray16,
        color: 'white',
        boxShadow: DEFAULT_BOX_SHADOW,
      },
    },
    mhVideoButton: {
      width: '64.8px',
      height: '64.8px',
      maxWidth: '64.8px',
      maxHeight: '64.8px',
      borderRadius: '50%',
      margin: '0 5px',
      cursor: 'pointer',
      '&:hover': {
        boxShadow:
          '0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12), 0 5px 5px 3px rgba(0,0,0,0.2)',
      },
    },
    mhVideoButtonSVG: {
      display: 'block',
      margin: 'auto',
      height: '100%',
      objectFit: 'contain',
      backgroundColor: 'transparent',
    },
    sidebar: {
      position: 'relative',
    },
  });

const getVideoSessionMutation = gql`
  mutation GetVideoSession($uuid: UUID!) {
    getVideoSession(uuid: $uuid) {
      videoSession {
        sessionId
        sessionToken
        apiKey
      }
    }
  }
`;

const markVideoSessionStart = gql`
  mutation MarkCoachingVisitStart($uuid: UUID!) {
    markCoachingVisitStart(uuid: $uuid) {
      ok
    }
  }
`;

const markVideoSessionEnd = gql`
  mutation MarkCoachingVisitEnd($uuid: UUID!) {
    markCoachingVisitEnd(uuid: $uuid) {
      ok
    }
  }
`;

const getCoachingVisit = gql`
  query coachingVisit($uuid: UUID!) {
    coachingVisit(uuid: $uuid) {
      uuid
      scheduledDuration
      actualDuration
      scheduledTime
      isTherapy
      isCoupleSession
      visitInfo
      organizationImprovements
      previousProviderReview {
        abilityToHelpClient
        workedOnAgreedUponGoals
        clearPlanToWorkTowardGoals
        feedbackText
      }
      state
      videoSessionUrl
      coachAssignment {
        uuid
        active
        coach {
          firstName
          lastName
          uuid
        }
        member {
          fullName
          id
          uuid
          firstName
          lastName
        }
        conversation {
          uuid
        }
      }
    }
  }
`;

interface MatchParams {
  visitUuid: string;
}

type Props = RouteComponentProps<MatchParams>;

enum VideoChatState {
  NOT_READY = 'NOT_READY',
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  VIDEO_COMPLETED = 'VIDEO_COMPLETED',
  REPORT_COMPLETED = 'REPORT_COMPLETED',
}

interface SessionDataQueryResult {
  coachingVisit: {
    zoomUrl: string;
  };
}

interface SessionDataQueryVars {
  uuid?: string;
}

const VideoPage: React.FC<Props & WithStyles<typeof styles>> = ({ match, history, classes }) => {
  const visitUuid: string = match.params.visitUuid;
  const { videoProvider } = useFlags();

  const matches = useMediaQuery(`(max-width:${MIN_WIDTH_SCREEN_SIZE})`);

  const [error, setError] = useState('');
  const [sessionCredentials, setSessionCredentials] = useState({
    apiKey: null,
    sessionId: null,
    sessionToken: null,
  });

  const [showChat, setShowChat] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const [videoState, setVideoState] = useState(VideoChatState.NOT_READY);
  // TODO: these should be typed properly, needed to upgrade TS
  const [visit, setVisit] = useState<any>();
  const [coachAssignment, setCoachAssignment] = useState<any>();

  const dispatch = useDispatch<Dispatch>();
  const isZoomProvider =
    videoProvider === VideoProvider.ZoomLink || videoProvider === VideoProvider.ZoomEmbedded;

  useQuery<SessionDataQueryResult, SessionDataQueryVars>(WAITING_FOR_CLIENT_PAGE_QUERY, {
    client: memberClient,
    variables: {
      uuid: visitUuid,
    },
    onCompleted: (data) => {
      if (isZoomProvider) {
        if (data?.coachingVisit.zoomUrl) {
          window.open(data.coachingVisit.zoomUrl, '_blank');
          history.push('/sessions');
        } else {
          Sentry.captureMessage(`Empty zoomUrl for session ${visitUuid}`, Severity.Error);
        }
      }
    },
  });

  useEffect(() => {
    if (isZoomProvider) {
      return;
    }
    memberClient
      .mutate({
        mutation: getVideoSessionMutation,
        variables: { uuid: visitUuid },
      })
      .then((response) => {
        setSessionCredentials(response.data.getVideoSession.videoSession);
        setVideoState(VideoChatState.READY);
      })
      .catch((error) => {
        if (error.graphQLErrors?.length) {
          // only setting the first error as its irrelevant to show all - one error
          // prevents the session from occuring.
          setError(error.graphQLErrors[0].message);
        } else if (error.networkError) {
          setError(error.message);
        } else {
          setError('There was an error initializing the video session. Please refresh.');
        }
      });
  }, [visitUuid, isZoomProvider]);

  useEffect(() => {
    if (isZoomProvider) {
      return;
    }
    memberClient
      .query({
        query: getCoachingVisit,
        variables: { uuid: visitUuid },
        fetchPolicy: 'network-only',
      })
      .then((response) => {
        setVisit(response.data.coachingVisit);
        setCoachAssignment(response.data.coachingVisit.coachAssignment);
      })
      .catch((error) => {
        setError(error.message);
      });
  }, [visitUuid, isZoomProvider]);

  const onError = (err: { message: string }) => {
    setError(`Failed to connect: ${err.message}`);
  };

  const connect = () => {
    setVideoState(VideoChatState.IN_PROGRESS);
    dispatch.presence.updatePresence(PresenceState.Offline);
    setShowProfile(true);
    memberClient
      .mutate({
        mutation: markVideoSessionStart,
        variables: { uuid: visitUuid },
      })
      .catch((error) => {
        if (error.graphQLErrors?.length) {
          // only setting the first error as its irrelevant to show all - one error
          // prevents the session from occuring.
          setError(error.graphQLErrors[0].message);
        } else {
          setError('There was an error.');
        }
      });
  };

  const disconnect = () => {
    if (visit) {
      // pre-populating so that when the report visit is completed, it is done
      // with the inferred values (which can be over-riden by the provider)
      visit.state = 'COMPLETED';
      visit.modalityType = 'VIDEO';
    }
    setVideoState(VideoChatState.VIDEO_COMPLETED);

    memberClient
      .mutate({
        mutation: markVideoSessionEnd,
        variables: { uuid: visitUuid },
      })
      .catch((error) => {
        if (error.graphQLErrors?.length) {
          // only setting the first error as its irrelevant to show all - one error
          // prevents the session from occuring.
          setError(error.graphQLErrors[0].message);
        } else {
          setError('There was an error.');
        }
      });
  };

  const routeToSessionsPage = () => {
    history.push('/sessions');
  };

  const afterReportVisit = () => {
    setVideoState(VideoChatState.REPORT_COMPLETED);
  };

  const reloadPage = () => history.go(0);

  const afterConfirmation = () => {
    history.push(buildMemberChatPath(coachAssignment.conversation.uuid, true));
  };

  const toggleShowChat = () => {
    if (showChat) {
      setShowChat(false);
    } else {
      if (showProfile) {
        setShowProfile(false);
      }
      setShowChat(true);
    }
  };

  const toggleShowProfile = () => {
    if (showProfile) {
      setShowProfile(false);
    } else {
      if (showChat) {
        setShowChat(false);
      }
      setShowProfile(true);
    }
  };

  const { apiKey, sessionId, sessionToken } = sessionCredentials;

  if (matches) {
    return (
      <DefaultError
        mainMessage="Sorry, video sessions are only available on tablets and desktop computers."
        subMessage="Please join the video session on a bigger device."
      />
    );
  }

  if (error) {
    return (
      <DefaultError mainMessage={error} subMessage="Please try to join the video session again.">
        <div className={classes.errorButtons}>
          <MHButton onClick={routeToSessionsPage} extraClasses={[classes.endVideoSessionButton]}>
            End Video Session
          </MHButton>
          {/* history.go */}
          <MHButton onClick={reloadPage} extraClasses={[classes.reconnectButton]}>
            <RefreshIcon />
            Reconnect
          </MHButton>
        </div>
      </DefaultError>
    );
  }

  if (!visit || !coachAssignment || !sessionCredentials.apiKey) {
    return (
      <PageContainer>
        <h1>Loading...</h1>
      </PageContainer>
    );
  }

  return (
    <PageContainer showSidebar={false}>
      {[VideoChatState.NOT_READY, VideoChatState.READY].includes(videoState) ? (
        <VideoPrompt
          // this disables the button to join the session, which will not be available
          // until the session credentials are loaded
          joinSessionDisabled={videoState === VideoChatState.NOT_READY}
          onJoinSession={connect}
          onGoBack={routeToSessionsPage}
          sessionStartTime={visit.scheduledTime}
          clientName={coachAssignment.member.fullName}
        />
      ) : videoState === VideoChatState.IN_PROGRESS ? (
        <VideoSession
          apiKey={apiKey}
          sessionId={sessionId}
          sessionToken={sessionToken}
          onDisconnect={disconnect}
          onError={onError}
          toggleShowChat={toggleShowChat}
          toggleShowProfile={toggleShowProfile}
        />
      ) : videoState === VideoChatState.VIDEO_COMPLETED ? (
        <ReportVisitDialog
          coachAssignment={coachAssignment}
          visit={visit}
          onCancel={routeToSessionsPage}
          onSuccess={afterReportVisit}
        />
      ) : videoState === VideoChatState.REPORT_COMPLETED ? (
        <ReportVisitConfirmation
          onRedirectToUserProfile={afterConfirmation}
          onClose={routeToSessionsPage}
          closeDialog={routeToSessionsPage}
        />
      ) : (
        <h1>There is an unexplained error...</h1>
      )}
      {showChat && <h1>Chat Placholder</h1>}
      {showProfile && coachAssignment.member && (
        <MemberProfileDrawer
          className={classes.sidebar}
          onClickExit={() => setShowProfile(false)}
          memberUuid={coachAssignment.member.uuid}
          disableReportVisit
        />
      )}
    </PageContainer>
  );
};

export default compose(withApollo, withStyles(styles))(VideoPage);
